import { type FC, type ReactNode, createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

import { JobStatus, useJobContext } from '@/providers/Jobs';
import SocketContext, { SocketMessageType } from '@/providers/Socket/SocketContext';

const JOB_REMOVAL_DELAY_MS = 3000;

export type CompletedDocumentData = {
  createdAt: string;
  documentTitle: string;
};

const getDocumentKey = (userWorkflowId: string, documentTitle: string): string => `${userWorkflowId}:${documentTitle}`;

const clearEntriesForWorkflow = <T,>(map: Map<string, T>, userWorkflowId: string): Map<string, T> => {
  const updated = new Map(map);
  const prefix = `${userWorkflowId}:`;
  Array.from(updated.keys()).forEach((key) => {
    if (key.startsWith(prefix)) {
      updated.delete(key);
    }
  });
  return updated;
};

type RegeneratingDocumentsContextType = {
  getCompletedDocumentData: (
    userWorkflowId: string,
    documentTitle: string | null | undefined,
  ) => CompletedDocumentData | undefined;
  isDocumentRegenerating: (userWorkflowId: string, documentTitle: string | null | undefined) => boolean;
  registerDocumentJob: (userWorkflowId: string, documentTitle: string, jobId: string) => void;
};

const RegeneratingDocumentsContext = createContext<RegeneratingDocumentsContextType>({
  getCompletedDocumentData: () => undefined,
  isDocumentRegenerating: () => false,
  registerDocumentJob: () => {},
});

type RegeneratingDocumentsProviderProps = {
  children: ReactNode;
};

type RegenerationStatusMessage = {
  completedDocuments?: CompletedDocumentData[];
  documentTitles: string[];
  status: 'started' | 'completed';
  userWorkflowId: string;
};

export const RegeneratingDocumentsProvider: FC<RegeneratingDocumentsProviderProps> = ({ children }) => {
  const { addListener, removeListener } = useContext(SocketContext);
  const { updateJob, removeJob } = useJobContext();
  const [regeneratingDocumentsMap, setRegeneratingDocumentsMap] = useState<Map<string, Set<string>>>(new Map());
  // Store completed document data for immediate display
  // Key format: `${userWorkflowId}:${documentTitle}`
  const [completedDocumentDataMap, setCompletedDocumentDataMap] = useState<Map<string, CompletedDocumentData>>(
    new Map(),
  );
  // Store job IDs for document regeneration jobs
  // Key format: `${userWorkflowId}:${documentTitle}` -> jobId
  const documentJobMapRef = useRef<Map<string, string>>(new Map());

  useEffect(() => {
    const listenerId = addListener({
      fn: (data: unknown) => {
        const message = data as RegenerationStatusMessage;
        const { userWorkflowId, documentTitles, completedDocuments, status } = message;

        if (status === 'started') {
          // Clear any stale completed document data for this workflow
          setCompletedDocumentDataMap((previous) => clearEntriesForWorkflow(previous, userWorkflowId));
          setRegeneratingDocumentsMap((previous) => {
            const updated = new Map(previous);
            const existingSet = updated.get(userWorkflowId) ?? new Set();
            const newSet = new Set(existingSet);
            documentTitles.forEach((title) => newSet.add(title));
            updated.set(userWorkflowId, newSet);
            return updated;
          });
        } else if (status === 'completed') {
          // Store completed document data for immediate display
          if (completedDocuments && completedDocuments.length > 0) {
            setCompletedDocumentDataMap((previous) => {
              const updated = new Map(previous);
              completedDocuments.forEach((doc) => {
                updated.set(getDocumentKey(userWorkflowId, doc.documentTitle), doc);
              });
              return updated;
            });
          }

          // Update and remove jobs for completed documents
          documentTitles.forEach((documentTitle) => {
            const jobKey = getDocumentKey(userWorkflowId, documentTitle);
            const jobId = documentJobMapRef.current.get(jobKey);
            if (jobId) {
              updateJob({
                id: jobId,
                status: JobStatus.COMPLETED,
              });
              setTimeout(() => removeJob({ id: jobId } as Parameters<typeof removeJob>[0]), JOB_REMOVAL_DELAY_MS);
              documentJobMapRef.current.delete(jobKey);
            }
          });

          setRegeneratingDocumentsMap((previous) => {
            const updated = new Map(previous);
            const existingSet = updated.get(userWorkflowId);
            if (existingSet) {
              const newSet = new Set(existingSet);
              documentTitles.forEach((title) => newSet.delete(title));
              if (newSet.size === 0) {
                updated.delete(userWorkflowId);
              } else {
                updated.set(userWorkflowId, newSet);
              }
            }
            return updated;
          });
        }
      },
      messageType: SocketMessageType.DOCUMENTS_REGENERATION_STATUS,
    });

    return () => {
      removeListener(listenerId);
    };
  }, [addListener, removeListener, updateJob, removeJob]);

  const registerDocumentJob = useCallback((userWorkflowId: string, documentTitle: string, jobId: string) => {
    documentJobMapRef.current.set(getDocumentKey(userWorkflowId, documentTitle), jobId);
  }, []);

  const isDocumentRegenerating = useCallback(
    (userWorkflowId: string, documentTitle: string | null | undefined): boolean => {
      if (!documentTitle || !userWorkflowId) return false;
      const documentsSet = regeneratingDocumentsMap.get(userWorkflowId);
      return documentsSet?.has(documentTitle) ?? false;
    },
    [regeneratingDocumentsMap],
  );

  const getCompletedDocumentData = useCallback(
    (userWorkflowId: string, documentTitle: string | null | undefined): CompletedDocumentData | undefined => {
      if (!documentTitle || !userWorkflowId) return undefined;
      return completedDocumentDataMap.get(getDocumentKey(userWorkflowId, documentTitle));
    },
    [completedDocumentDataMap],
  );

  return (
    <RegeneratingDocumentsContext.Provider
      value={{
        getCompletedDocumentData,
        isDocumentRegenerating,
        registerDocumentJob,
      }}
    >
      {children}
    </RegeneratingDocumentsContext.Provider>
  );
};

export const useRegeneratingDocuments = (): RegeneratingDocumentsContextType =>
  useContext(RegeneratingDocumentsContext);
